import { z } from "zod";
import { HEX_COLOR_REGEX } from "@common/constants/invoiceDesign";
import {
    NATIVE_INVOICE_ADJUSTMENT_MODES,
    NATIVE_INVOICE_ADJUSTMENT_SIGNS,
    NATIVE_INVOICE_DEFAULT_ADJUSTMENT_MODE,
    NATIVE_INVOICE_DEFAULT_DEPOSIT_SIGN,
    NATIVE_INVOICE_DEFAULT_DISCOUNT_SIGN,
    NATIVE_INVOICE_DEFAULT_TAX_SIGN,
    NATIVE_INVOICE_FONT_FAMILIES,
    NATIVE_INVOICE_FOOTER_ALIGNS,
    NATIVE_INVOICE_LABEL_KEYS,
    NATIVE_INVOICE_PAGE_SIZES,
} from "@common/constants/nativeInvoice";
import {
    isSupportedNativeInvoicePaymentMethods,
    NativeInvoicePaymentMethod,
} from "@common/constants/nativeInvoicePaymentMethod";
import { isDateOnlyBefore, isoDateSchema } from "@common/helpers/date";
import { InvoiceStatus } from "@common/types/invoice";
import { NativeInvoiceTermType } from "@common/helpers/invoice-terms.helper";
import { accountOverridesRequestSchema } from "@common/schemas/domains/accounting/account-override.schema";
import { httpsUrlSchema } from "@common/schemas/shared.schema";
import {
    nativeInvoiceCustomTermSchema,
    nativeInvoiceDueDateRuleFieldsSchema,
    nativeInvoiceEmailIntentFieldsSchema,
    refineNativeInvoiceDueDateRule,
} from "./native-invoice-template-intent.schema";
import { nativeInvoicePdfFieldSettingsSchema } from "./native-invoice-field-settings.schema";
import {
    nativeInvoiceAdjustmentSettingsInputSchema,
    nativeInvoiceAdjustmentSettingsResponseSchema,
} from "./native-invoice-adjustment-settings.schema";

// Upper bound for monetary values stored in decimal(20,2) columns.
// Capped well below Number.MAX_SAFE_INTEGER to avoid JS floating-point
// precision loss during intermediate calculations.
const MONETARY_MAX = 999_999_999_999;
export const NATIVE_INVOICE_EMAIL_SUBJECT_MAX_LENGTH = 255;
export const NATIVE_INVOICE_EMAIL_MESSAGE_MAX_LENGTH = 5000;
const QUANTITY_MAX = 999_999;

const monetarySchema = z.number().finite().min(-MONETARY_MAX).max(MONETARY_MAX);
const quantitySchema = z.number().finite().min(-QUANTITY_MAX).max(QUANTITY_MAX);

export const nativeInvoiceAddressSchema = z
    .object({
        street: z.string().max(255).nullable(),
        street2: z.string().max(255).nullable(),
        city: z.string().max(255).nullable(),
        state: z.string().max(255).nullable(),
        postalCode: z.string().max(50).nullable(),
        country: z.string().max(255).nullable(),
    })
    .strict();

export const nativeInvoicePartySchema = z
    .object({
        businessName: z.string().max(255).nullable(),
        company: z.string().max(255).nullable().optional(),
        name: z.string().max(255).nullable(),
        email: z.string().email().nullable(),
        phoneNumber: z.string().max(50).nullable(),
        taxId: z.string().max(255).nullable().optional(),
        address: nativeInvoiceAddressSchema,
    })
    .strict();

export const nativeInvoiceLineInputSchema = z
    .object({
        id: z.string().uuid().optional(),
        // Optional backlink to a catalog item/price (presentation/catalog
        // concern; the snapshot still stores resolved name/amount so historical
        // render is independent of the live item row).
        catalogItemId: z.string().uuid().nullable().optional(),
        catalogItemPriceId: z.string().uuid().nullable().optional(),
        productOrService: z.string().max(255).nullable(),
        description: z.string().max(1000).nullable(),
        quantity: quantitySchema.nullable(),
        unitAmount: monetarySchema.nullable(),
        amount: monetarySchema,
        discount: monetarySchema.default(0),
        tax: monetarySchema.default(0),
        deposit: monetarySchema.default(0),
        discountMode: z
            .enum(NATIVE_INVOICE_ADJUSTMENT_MODES)
            .nullable()
            .optional(),
        taxMode: z.enum(NATIVE_INVOICE_ADJUSTMENT_MODES).nullable().optional(),
        depositMode: z
            .enum(NATIVE_INVOICE_ADJUSTMENT_MODES)
            .nullable()
            .optional(),
        serviceDate: isoDateSchema.nullable().optional(),
        sku: z.string().max(255).nullable().optional(),
        // PRO-12203: these three flags used to signal a server-side
        // catalog-item write-back (line edits overwriting the item's
        // description/price). That write-back has been removed entirely —
        // these fields are accepted (so the strict schema doesn't 400 an
        // older, not-yet-updated client during a rolling deploy) but are
        // otherwise inert and never read or persisted.
        writeBackToCatalogItem: z.boolean().optional(),
        writeBackDescription: z.boolean().optional(),
        writeBackUnitAmount: z.boolean().optional(),
        // Transient backlink to the billable-expense transaction this line was
        // prefilled from (create-time "invoice these expenses" flow only — see
        // buildBillableExpenseInvoicePrefill). Never persisted (no matching
        // column on native_invoice_line_item_detail, so a reloaded/edited
        // invoice's lines never carry it back). Lets the backend correlate
        // `billableExpenseTransactionIds` to a specific line so it can (a)
        // reject a quantity/rate edit on that line and (b) exclude a
        // transaction from linking when its line was removed before save. See
        // NativeInvoiceBillableExpenseInternalService#assertTransactionsCanBeBilled.
        billableExpenseTransactionId: z
            .number()
            .int()
            .positive()
            .nullable()
            .optional(),
    })
    .strict();

// Per-line accounting coding (Class + Income account + Receivables account),
// kept OUT of the render snapshot: the snapshot is presentational (feeds the
// PDF and the change-detection hash), while coding is an accounting concern
// persisted via the audited InvoiceLineItemService path. Keyed by the snapshot
// line `id` (which becomes `invoice_line_item.sourceId`) so entries correlate
// to lines across create/update the same way the snapshot's own line ids do.
// Reuses `accountOverridesRequestSchema` (per-ledger overrides) rather than the
// deprecated legacy `accountId` / `accountsReceivableId` fields. The drawer
// writes the income account onto the entity's primary accrual ledger when one
// exists, otherwise the primary cash ledger, and writes receivables only onto
// the primary accrual ledger. The wire format is multi-ledger ready and
// matches `updateInvoiceLineItem` (which accepts both `accountOverrides` and
// `accountsReceivableOverrides`).
export const nativeInvoiceLineAccountingEntrySchema = z.object({
    lineId: z.string().uuid(),
    classIds: z.array(z.string().uuid()).max(100).optional(),
    accountOverrides: accountOverridesRequestSchema.optional(),
    accountsReceivableOverrides: accountOverridesRequestSchema.optional(),
});

export const nativeInvoiceLineAccountingSchema = z
    .array(nativeInvoiceLineAccountingEntrySchema)
    .max(100);

export function parseNativeInvoiceEmailRecipients(
    value: string | null | undefined,
): string[] {
    return (value ?? "")
        .split(",")
        .map((email) => email.trim())
        .filter(Boolean);
}

export function hasNativeInvoiceEmailRecipients(
    value: string | null | undefined,
): boolean {
    return parseNativeInvoiceEmailRecipients(value).length > 0;
}

interface EmailRecipientListSchemaOptions {
    minRecipients: number;
}

function buildEmailRecipientListSchema({
    minRecipients,
}: EmailRecipientListSchemaOptions) {
    return z.string().superRefine((value, context) => {
        const emails = parseNativeInvoiceEmailRecipients(value);
        const emailSchema = z.string().email();
        if (
            emails.length < minRecipients ||
            emails.length > 50 ||
            emails.some((email) => !emailSchema.safeParse(email).success)
        ) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Invalid email recipient list",
            });
        }
    });
}

// Send-ready email: a recipient and a subject are required because this is what
// the send / schedule endpoints actually deliver.
export const nativeInvoiceEmailSnapshotSchema = z
    .object({
        to: buildEmailRecipientListSchema({ minRecipients: 1 }),
        replyTo: z.string().email().nullable().optional(),
        cc: z.array(z.string().email()).max(50).optional(),
        bcc: z.array(z.string().email()).max(50).optional(),
        subject: z.string().min(1).max(NATIVE_INVOICE_EMAIL_SUBJECT_MAX_LENGTH),
        message: z.string().max(NATIVE_INVOICE_EMAIL_MESSAGE_MAX_LENGTH),
        attachPdf: z.boolean().default(true),
        scheduledSendAt: z.string().datetime().nullable(),
        // Null / omitted = Kick's invoices@kick.co. A uuid must be a sending
        // email owned by the invoice's workspace.
        sendingEmailId: z.string().uuid().nullable().optional(),
    })
    .strict();

// Persisted email: a draft (and every invoice template, which has no recipient
// of its own until it is applied) can carry composed copy before it is
// addressable. An omitted snapshot clears the stored email columns, so the
// recipient/subject requirements have to relax here or that copy would be
// dropped on every save. Used for the create/update bodies and for anything
// that returns what was stored.
export const nativeInvoiceStoredEmailSnapshotSchema =
    nativeInvoiceEmailSnapshotSchema.extend({
        to: buildEmailRecipientListSchema({ minRecipients: 0 }),
        subject: z.string().max(NATIVE_INVOICE_EMAIL_SUBJECT_MAX_LENGTH),
    });

const roundMonetary = (value: number) => Math.round(value * 100) / 100;

const nativeInvoiceRenderSnapshotBaseSchema = z.object({
    from: nativeInvoicePartySchema,
    to: nativeInvoicePartySchema,
    invoiceNumber: z.string().max(255).nullable(),
    invoiceDate: isoDateSchema,
    dueDate: isoDateSchema.nullable(),
    poNumber: z.string().max(255).nullable(),
    note: z.string().max(2000).nullable(),
    terms: z.string().max(2000).nullable(),
    footer: z.string().max(2000).nullable().optional(),
    footerAlign: z.enum(NATIVE_INVOICE_FOOTER_ALIGNS).nullable().optional(),
    // Page-count indicator ("1 of 2") on the right side of the footer strip.
    // Omitted on older clients; defaults to enabled.
    showPagination: z.boolean().optional(),
    // Omitted on older clients; the assembler / PDF renderer fall back to
    // NATIVE_INVOICE_DEFAULT_PAGE_SIZE (A4, the previous hardcoded format).
    pageSize: z.enum(NATIVE_INVOICE_PAGE_SIZES).optional(),
    currency: z
        .string()
        .regex(/^[a-zA-Z]{3}$/)
        .transform((value) => value.toLowerCase()),
    subtotal: monetarySchema,
    // Kind-native signed computed amounts (percent rows resolve to the dollar
    // amount of the subtotal). A minus flips that row's default direction.
    tax: monetarySchema,
    discount: monetarySchema,
    deposit: monetarySchema,
    // The %/amount choice and the raw value the user entered, persisted so an
    // edited invoice can restore the toggle and original input. Required with a
    // default so every persisted invoice carries an explicit mode/sign — there
    // is no pre-launch corpus of snapshots that predate these fields.
    taxMode: z
        .enum(NATIVE_INVOICE_ADJUSTMENT_MODES)
        .default(NATIVE_INVOICE_DEFAULT_ADJUSTMENT_MODE),
    discountMode: z
        .enum(NATIVE_INVOICE_ADJUSTMENT_MODES)
        .default(NATIVE_INVOICE_DEFAULT_ADJUSTMENT_MODE),
    depositMode: z
        .enum(NATIVE_INVOICE_ADJUSTMENT_MODES)
        .default(NATIVE_INVOICE_DEFAULT_ADJUSTMENT_MODE),
    // Whether each adjustment type is entered per line item instead of once
    // at the header level. A type is never both — line items only carry a
    // non-zero value for a type when its header flag is true, and the header
    // amount is only spread/split across lines when its flag is false (see
    // NativeInvoiceLinesInternalService / the tax-discount-deposit splitter).
    isTaxPerLineItem: z.boolean().default(false),
    isDiscountPerLineItem: z.boolean().default(false),
    isDepositPerLineItem: z.boolean().default(false),
    // Whether each adjustment's per-line value is also rendered as its own
    // column in the PDF's line-items table (Item/Quantity/Rate/Tax/Discount/
    // Deposit/Amount — see NATIVE_INVOICE_PDF_ITEMS_TABLE_FIELD_IDS for the
    // print order). Distinct from `isXPerLineItem` (which controls where the
    // VALUE is computed): a type can be calculated per line without printing
    // a column for it. Only meaningful when the matching `isXPerLineItem` is
    // also true — every per-line value is 0 otherwise, so the column would
    // print nothing — enforced at the request boundary (see
    // refineNativeInvoiceRenderSnapshotInput) and in the drawer UI (the
    // checkbox is disabled while calculation is header-level).
    isTaxDisplayedPerLineItem: z.boolean().default(false),
    isDiscountDisplayedPerLineItem: z.boolean().default(false),
    isDepositDisplayedPerLineItem: z.boolean().default(false),
    // Whether each adjustment row adds to ("positive") or subtracts from
    // ("negative") the total. Tax adds; discount and deposit subtract.
    taxSign: z
        .enum(NATIVE_INVOICE_ADJUSTMENT_SIGNS)
        .default(NATIVE_INVOICE_DEFAULT_TAX_SIGN),
    discountSign: z
        .enum(NATIVE_INVOICE_ADJUSTMENT_SIGNS)
        .default(NATIVE_INVOICE_DEFAULT_DISCOUNT_SIGN),
    depositSign: z
        .enum(NATIVE_INVOICE_ADJUSTMENT_SIGNS)
        .default(NATIVE_INVOICE_DEFAULT_DEPOSIT_SIGN),
    // The raw value the user entered per adjustment row; null → the assembler
    // falls back to the computed monetary amount on `invoice`.
    taxInput: monetarySchema.nullable().optional(),
    discountInput: monetarySchema.nullable().optional(),
    depositInput: monetarySchema.nullable().optional(),
    total: monetarySchema,
    lines: z.array(nativeInvoiceLineInputSchema).min(1).max(100),
    // Client-computed monogram text shown when no logo is set; the renderer
    // falls back to the server-stamped `monogram` and then to a name-derived
    // value, so it is not persisted (D-note in §5.2 of the design).
    initials: z.string().max(8).nullable().optional().default(null),
    // Design step branding fields. `logoPath` is the S3 object key in the
    // public bucket (resolved to a URL server-side via s3.buildPublicUrl, so we
    // never persist/trust a client-supplied URL). `backgroundColor` /
    // `accentColor` / `textColor` are hex strings (optionally `#`-prefixed),
    // `fontFamily` is an invoice font id resolved via
    // common/constants/invoiceDesign.
    logoPath: z.string().max(1024).nullable().optional().default(null),
    backgroundColor: z
        .string()
        .regex(HEX_COLOR_REGEX)
        .nullable()
        .optional()
        .default(null),
    accentColor: z.string().regex(HEX_COLOR_REGEX).nullable().optional(),
    // Overrides the document's primary text color (body copy, headings). Null
    // = the near-black default (see INVOICE_DEFAULT_TEXT_COLOR) — needed so a
    // dark custom background stays legible instead of dark-on-dark.
    textColor: z.string().regex(HEX_COLOR_REGEX).nullable().optional(),
    paymentButtonBackgroundColor: z
        .string()
        .regex(HEX_COLOR_REGEX)
        .nullable()
        .optional(),
    customPaymentLink: z
        .string()
        .max(2048)
        .pipe(httpsUrlSchema)
        .nullable()
        .optional(),
    fontFamily: z
        .enum(NATIVE_INVOICE_FONT_FAMILIES)
        .nullable()
        .optional()
        .default(null),
    // Branding is server-authoritative: the API derives these from the sending
    // entity (`EntityEntity.logo` / `EntityEntity.name`) on create/update and
    // stores them on the snapshot so PDFs render deterministically without a
    // live entity lookup at download time. They live on the stored snapshot
    // (and the preview response) but are stripped from the client request body
    // — see `nativeInvoiceRenderSnapshotInputSchema` below.
    logoUrl: z.string().nullable().optional(),
    monogram: z.string().max(8).nullable().optional(),
    // User-customizable PDF field labels (Design step). Keyed by the fixed
    // label-key enum; a key omitted from the record falls back to
    // NATIVE_INVOICE_LABEL_DEFAULTS at render time — see
    // NativeInvoiceHtmlInternalService. Tri-state on update, deliberately NOT
    // defaulted to null: omitting the field entirely leaves any saved overrides
    // untouched (so a client that predates this field can save unrelated edits
    // without clearing them — see applyRenderSnapshotToDetail), an explicit
    // `null` clears them, and a record replaces the saved set wholesale.
    labelOverrides: z
        .record(
            z.enum(NATIVE_INVOICE_LABEL_KEYS),
            z.string().trim().min(1).max(100),
        )
        .nullable()
        .optional(),
    // Fields & visibility (Header / From / To / Details / Items /
    // Totals). Same
    // tri-state as labelOverrides so an older client can save unrelated edits
    // without clearing a saved visibility set.
    fieldSettings: nativeInvoicePdfFieldSettingsSchema.nullable().optional(),
});

function refineNativeInvoiceRenderSnapshot(
    data: {
        subtotal: number;
        tax: number;
        discount: number;
        deposit: number;
        total: number;
        isTaxPerLineItem: boolean;
        isDiscountPerLineItem: boolean;
        isDepositPerLineItem: boolean;
        lines: Array<{
            id?: string;
            tax: number;
            discount: number;
            deposit: number;
        }>;
    },
    ctx: z.RefinementCtx,
) {
    // Tax/discount/deposit are kind-native signed: a minus flips that row's
    // default direction (tax credit, discount surcharge, deposit reversal).
    // Subtotal and total stay non-negative.
    const nonNegativeHeaderFields = ["subtotal", "total"] as const;
    for (const field of nonNegativeHeaderFields) {
        if (data[field] < 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: `${field} must be non-negative`,
            });
        }
    }

    const taxForTotal = data.isTaxPerLineItem
        ? data.lines.reduce((sum, line) => sum + line.tax, 0)
        : data.tax;
    const discountForTotal = data.isDiscountPerLineItem
        ? data.lines.reduce((sum, line) => sum + line.discount, 0)
        : data.discount;
    const depositForTotal = data.isDepositPerLineItem
        ? data.lines.reduce((sum, line) => sum + line.deposit, 0)
        : data.deposit;
    const expectedTotal = roundMonetary(
        data.subtotal + taxForTotal - discountForTotal - depositForTotal,
    );
    if (roundMonetary(data.total) !== expectedTotal) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["total"],
            message: "total must equal subtotal + tax - discount - deposit",
        });
    }

    const lineIds = data.lines
        .map((line) => line.id)
        .filter((id): id is string => id != null);
    if (new Set(lineIds).size !== lineIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["lines"],
            message: "line ids must be unique",
        });
    }
}

// Due date must be on or after the invoice date. This lives on the
// request-input refine only: `nativeInvoiceRenderSnapshotSchema` is also the
// `previewNativeInvoice` response schema (validated with `validateResponses`),
// and it reads back snapshots persisted before this rule existed — enforcing it
// there would turn every such invoice into a 500 that the drawer can never load,
// so the user could not even open it to correct the date.
function refineNativeInvoiceRenderSnapshotInput(
    data: {
        invoiceDate: string;
        dueDate: string | null;
        tax: number;
        discount: number;
        deposit: number;
        isTaxPerLineItem: boolean;
        isDiscountPerLineItem: boolean;
        isDepositPerLineItem: boolean;
        isTaxDisplayedPerLineItem: boolean;
        isDiscountDisplayedPerLineItem: boolean;
        isDepositDisplayedPerLineItem: boolean;
    },
    ctx: z.RefinementCtx,
) {
    if (
        data.dueDate !== null &&
        isDateOnlyBefore(data.dueDate, data.invoiceDate)
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["dueDate"],
            message: "dueDate must be on or after invoiceDate",
        });
    }

    // Per-type mutual exclusivity: an adjustment is entered either once at the
    // header (proportionally spread across lines by the tax/discount/deposit
    // splitter) or per line item — never both. A non-zero header value carried
    // alongside its isXPerLineItem flag is under-booked: the splitter zeroes
    // that type's header spread and books the line amounts instead, so a
    // leftover header tax/total would not match the journal. Reject it at the
    // request boundary rather than persist the inconsistency.
    const perLineAdjustments = [
        {
            isPerLineItem: data.isTaxPerLineItem,
            headerValue: data.tax,
            field: "tax",
        },
        {
            isPerLineItem: data.isDiscountPerLineItem,
            headerValue: data.discount,
            field: "discount",
        },
        {
            isPerLineItem: data.isDepositPerLineItem,
            headerValue: data.deposit,
            field: "deposit",
        },
    ] as const;
    for (const { isPerLineItem, headerValue, field } of perLineAdjustments) {
        if (isPerLineItem && headerValue !== 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: `${field} must be 0 at the header when entered per line item`,
            });
        }
    }

    // The "display column per line item" toggle only makes sense once the
    // matching value is actually computed per line — with calculation still
    // header-level, every line's tax/discount/deposit is 0 and the column
    // would render nothing. Reject the combination at the request boundary
    // instead of silently printing an all-zero column.
    const displayAdjustments = [
        {
            isPerLineItem: data.isTaxPerLineItem,
            isDisplayedPerLineItem: data.isTaxDisplayedPerLineItem,
            field: "isTaxDisplayedPerLineItem",
        },
        {
            isPerLineItem: data.isDiscountPerLineItem,
            isDisplayedPerLineItem: data.isDiscountDisplayedPerLineItem,
            field: "isDiscountDisplayedPerLineItem",
        },
        {
            isPerLineItem: data.isDepositPerLineItem,
            isDisplayedPerLineItem: data.isDepositDisplayedPerLineItem,
            field: "isDepositDisplayedPerLineItem",
        },
    ] as const;
    for (const {
        isPerLineItem,
        isDisplayedPerLineItem,
        field,
    } of displayAdjustments) {
        if (isDisplayedPerLineItem && !isPerLineItem) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: `${field} requires the matching per-line-item calculation to be enabled`,
            });
        }
    }
}

export const nativeInvoiceRenderSnapshotSchema =
    nativeInvoiceRenderSnapshotBaseSchema
        .strict()
        .superRefine(refineNativeInvoiceRenderSnapshot);

// The client request body must not carry server-authoritative branding: the
// server always overwrites `logoUrl`/`monogram` from the entity, so accepting
// them from the client would be misleading (silently ignored) and let a caller
// think they could brand a PDF. Omitting them keeps the request contract honest.
// `customPaymentLink` is omitted too: it is written only via platform endpoints.
export const nativeInvoiceRenderSnapshotInputSchema =
    nativeInvoiceRenderSnapshotBaseSchema
        .omit({ logoUrl: true, monogram: true, customPaymentLink: true })
        .strict()
        .superRefine(refineNativeInvoiceRenderSnapshot)
        .superRefine(refineNativeInvoiceRenderSnapshotInput);

export const nativeInvoiceAcceptedPaymentMethodsSchema = z
    .array(z.nativeEnum(NativeInvoicePaymentMethod))
    .refine(isSupportedNativeInvoicePaymentMethods, {
        message: "Accept card payments to offer bank transfer",
    });

// Create/update may persist DRAFT (default) or issue as OPEN without
// sending. Send/schedule still own email delivery. PAID/VOID/etc. stay
// off this body so a caller cannot jump to a post-send lifecycle state.
export const nativeInvoiceDraftBodyObjectSchema = z
    .object({
        counterpartyId: z.string().uuid().nullable().optional(),
        financialAccountId: z.coerce.number().int().nullable().optional(),
        // Opt-in Stripe payment collection. `stripeFinancialAccountId` is the
        // Stripe FA (sourceType = stripe, same entity) whose connected account
        // collects the charge — distinct from `financialAccountId` (import/source
        // attachment). Both are validated in the service.
        collectPayment: z.boolean().optional(),
        stripeFinancialAccountId: z.coerce.number().int().nullable().optional(),
        // Methods offered on the pay page. Omitted keeps the stored value
        // (default: card only).
        acceptedPaymentMethods:
            nativeInvoiceAcceptedPaymentMethodsSchema.optional(),
        status: z.enum([InvoiceStatus.DRAFT, InvoiceStatus.OPEN]),
        renderSnapshot: nativeInvoiceRenderSnapshotInputSchema,
        emailSnapshot: nativeInvoiceStoredEmailSnapshotSchema
            .nullable()
            .optional(),
        // Lives on the body (not only the email snapshot) so a From-address choice
        // persists even when the compose fields are not yet valid enough to emit
        // an email snapshot. Null = Kick default.
        sendingEmailId: z.string().uuid().nullable().optional(),
        // Per-line Class / Accrual / Receivables coding, kept separate from the
        // render snapshot so coding-only edits don't churn the change-detection
        // hash or PDF. See nativeInvoiceLineAccountingSchema for the wire format.
        lineAccounting: nativeInvoiceLineAccountingSchema.optional(),
        // Per-type (tax / discount / deposit) Class / GL account / Receivables
        // account coding applied to the invoice's synthetic adjustment rows.
        // Same separation from the render snapshot as lineAccounting; omitted
        // types are left untouched and unset ones fall back to the role
        // defaults. See nativeInvoiceAdjustmentSettingsInputSchema.
        adjustmentSettings:
            nativeInvoiceAdjustmentSettingsInputSchema.optional(),
        // Revision lineage: when this Draft is created by revising an Open invoice,
        // it points back to that original invoice. The backend validates the target
        // (same workspace/entity, MANUAL source, OPEN status) and enforces one
        // direct revision per original via a unique partial index. See §5.2.
        revisedFromInvoiceId: z.string().uuid().nullable().optional(),
        // Create-only: attach these unbilled billable-expense transactions to the
        // new invoice (`transaction.billableExpenseInvoiceId`). Omitted from
        // updates — linking is not retried after the draft exists.
        billableExpenseTransactionIds: z
            .array(z.number().int().positive())
            .min(1)
            .max(100)
            .optional(),
        // Create-only lineage: stamped when a draft is first saved from a
        // template prefill. Update must not change it.
        sourceTemplateId: z.string().uuid().optional(),
    })
    .merge(nativeInvoiceDueDateRuleFieldsSchema)
    .merge(nativeInvoiceEmailIntentFieldsSchema);

export const nativeInvoiceDraftBodySchema =
    nativeInvoiceDraftBodyObjectSchema.superRefine(
        refineNativeInvoiceDueDateRule,
    );

// Only a Draft accepts a native drawer update, and a Draft always submits the
// full render snapshot — branding included. There is no design-only patch:
// every other status opens as a PDF preview and is revised rather than edited.
export const nativeInvoiceUpdateBodySchema = nativeInvoiceDraftBodyObjectSchema
    .omit({
        revisedFromInvoiceId: true,
        billableExpenseTransactionIds: true,
        sourceTemplateId: true,
    })
    .partial()
    .superRefine(refineNativeInvoiceDueDateRule)
    .refine(
        ({
            counterpartyId,
            financialAccountId,
            collectPayment,
            stripeFinancialAccountId,
            acceptedPaymentMethods,
            status,
            renderSnapshot,
            emailSnapshot,
            sendingEmailId,
            lineAccounting,
            adjustmentSettings,
            termType,
            customTerm,
            isEmailSubjectAutomatic,
            isEmailMessageAutomatic,
        }) =>
            counterpartyId !== undefined ||
            financialAccountId !== undefined ||
            collectPayment !== undefined ||
            stripeFinancialAccountId !== undefined ||
            acceptedPaymentMethods !== undefined ||
            status !== undefined ||
            renderSnapshot !== undefined ||
            emailSnapshot !== undefined ||
            sendingEmailId !== undefined ||
            lineAccounting !== undefined ||
            adjustmentSettings !== undefined ||
            termType !== undefined ||
            customTerm !== undefined ||
            isEmailSubjectAutomatic !== undefined ||
            isEmailMessageAutomatic !== undefined,
        {
            message: "At least one native invoice field must be provided",
        },
    );

export const nativeInvoiceSendBodySchema = z.object({
    emailSnapshot: nativeInvoiceEmailSnapshotSchema,
});

export const nativeInvoiceResendBodySchema = z.object({
    subject: z.string().min(1).max(NATIVE_INVOICE_EMAIL_SUBJECT_MAX_LENGTH),
    message: z.string().max(NATIVE_INVOICE_EMAIL_MESSAGE_MAX_LENGTH),
});

// Test-send mails the CURRENT persisted draft copy (subject/message/PDF).
// By default it goes to the requesting user's own account email, unmodified.
// `to` is an optional override so the user can preview the email at any
// address (e.g. a client-facing inbox to sanity-check rendering) without
// mutating the invoice's own recipient/emailTo field. Same comma-separated
// list as the compose To field.
export const nativeInvoiceTestSendBodySchema = z.object({
    to: buildEmailRecipientListSchema({ minRecipients: 1 }).optional(),
});

// Duplication takes no input — the new Draft is derived entirely from the
// source invoice server-side. The empty body keeps the contract explicit and
// leaves room for future duplication options without a breaking change.
export const nativeInvoiceDuplicateBodySchema = z.object({});

// Allow up to 60s of clock skew between client and server so a "send in
// 5 minutes" call submitted right at the boundary doesn't reject. Anything
// older than that is almost certainly a stale form or a misconfigured
// caller — accepting it would let the send_scheduled_native_invoices CLI
// pick the row up on its very next tick and bypass the direct-send flow's
// audit/telemetry.
const SCHEDULE_PAST_TOLERANCE_MS = 60_000;

export const nativeInvoiceScheduleBodySchema = z.object({
    emailSnapshot: nativeInvoiceEmailSnapshotSchema,
    scheduledSendAt: z.coerce
        .date()
        .refine(
            (value) =>
                value.getTime() >= Date.now() - SCHEDULE_PAST_TOLERANCE_MS,
            { message: "scheduledSendAt must be in the future" },
        ),
});

export const nativeInvoicePreviewResponseSchema = z.object({
    html: z.string(),
    renderSnapshot: nativeInvoiceRenderSnapshotSchema,
    // Server-resolved public URL for the snapshot's logoPath (or null). Resolved
    // here via S3Service.buildPublicUrl so the client never has to replicate the
    // bucket/region logic to display a saved logo in the live preview.
    logoUrl: z.string().nullable(),
    // Null = Kick default. Hydrates the send-step From picker on edit.
    sendingEmailId: z.string().uuid().nullable(),
    collectPayment: z.boolean(),
    stripeFinancialAccountId: z.number().int().nullable(),
    acceptedPaymentMethods: nativeInvoiceAcceptedPaymentMethodsSchema,
    paymentToken: z.string().uuid().nullable(),
    // Null when the invoice has never been addressed. Hydrates the resend
    // modal (From/To/subject/body) for OPEN invoices.
    emailSnapshot: nativeInvoiceStoredEmailSnapshotSchema.nullable(),
    termType: z.nativeEnum(NativeInvoiceTermType).nullable(),
    customTerm: nativeInvoiceCustomTermSchema.nullable(),
    isEmailSubjectAutomatic: z.boolean(),
    isEmailMessageAutomatic: z.boolean(),
    // Tax / discount / deposit coding (`null` per type = defaults apply).
    // Written through the create / update body's `adjustmentSettings`.
    adjustmentSettings: nativeInvoiceAdjustmentSettingsResponseSchema,
});

export const nativeInvoiceLogoUploadUrlBodySchema = z.object({
    name: z.string().min(1).max(255),
});

export const nativeInvoiceLogoUploadUrlResponseSchema = z.object({
    uploadUrl: z.string(),
    path: z.string(),
    publicUrl: z.string(),
});

export const lastInvoiceNumberQueryParamsSchema = z.object({
    // Optional entity scope: when provided, the last invoice number is looked
    // up only for this entity so each business entity gets its own sequence.
    // When absent, the lookup falls back to the workspace-wide behavior.
    entityId: z.coerce.number().optional(),
});
export type LastInvoiceNumberQueryParams = z.infer<
    typeof lastInvoiceNumberQueryParamsSchema
>;

export type NativeInvoiceAddress = z.infer<typeof nativeInvoiceAddressSchema>;
export type NativeInvoiceParty = z.infer<typeof nativeInvoicePartySchema>;
export type NativeInvoiceLineRenderSnapshot = z.infer<
    typeof nativeInvoiceLineInputSchema
>;
export type NativeInvoiceRenderSnapshot = z.infer<
    typeof nativeInvoiceRenderSnapshotSchema
>;
// The stored shape is the superset (same keys, looser bounds), so it types both
// what the drawer composes and what the API returns.
export type NativeInvoiceEmailSnapshot = z.infer<
    typeof nativeInvoiceStoredEmailSnapshotSchema
>;
export type NativeInvoiceLineAccountingEntry = z.infer<
    typeof nativeInvoiceLineAccountingEntrySchema
>;
export type NativeInvoiceLineAccounting = z.infer<
    typeof nativeInvoiceLineAccountingSchema
>;
export type NativeInvoiceDraftBody = z.infer<
    typeof nativeInvoiceDraftBodySchema
>;
export type NativeInvoiceUpdateBody = z.infer<
    typeof nativeInvoiceUpdateBodySchema
>;
export type NativeInvoiceSendBody = z.infer<typeof nativeInvoiceSendBodySchema>;
export type NativeInvoiceResendBody = z.infer<
    typeof nativeInvoiceResendBodySchema
>;
export type NativeInvoiceTestSendBody = z.infer<
    typeof nativeInvoiceTestSendBodySchema
>;
export type NativeInvoiceDuplicateBody = z.infer<
    typeof nativeInvoiceDuplicateBodySchema
>;
export type NativeInvoiceScheduleBody = z.infer<
    typeof nativeInvoiceScheduleBodySchema
>;
export type NativeInvoiceLogoUploadUrlBody = z.infer<
    typeof nativeInvoiceLogoUploadUrlBodySchema
>;
export type NativeInvoiceLogoUploadUrlResponse = z.infer<
    typeof nativeInvoiceLogoUploadUrlResponseSchema
>;
